import React, { useEffect, useState, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Routes, Route, useNavigate, useLocation } from "react-router-dom";
import Icon from "@mdi/react";
import {
  mdiMenu,
  mdiMenuClose,
  mdiDotsVertical,
  mdiTranslate,
  mdiLogout,
} from "@mdi/js";
import { RootState, store, AppDispatch } from "./store";
import {
  setLanguage,
  fetchTextLabels,
  toggleUtilityCardMinimized,
} from "./store/uiSlice";
import {
  fetchGateways,
  fetchMonitoredGateways,
  fetchMapGateways,
} from "./store/gatewaysSlice";
import {
  fetchConfiguredDevices,
  updateDevicesFromApi,
  fetchMonitoredDevices,
  updateDeviceFromSSE,
  recalculateDevicePositions,
} from "./store/devicesSlice";
import { fetchSettings } from "./store/settingsSlice";
import { logout } from "./store/authSlice";
import { resetMapsState } from "./store/mapsSlice";
import { resetDevicesState } from "./store/devicesSlice";
import { resetGatewaysState } from "./store/gatewaysSlice";
import {
  fetchMaps,
  fetchSharedMaps,
  selectCurrentMap,
  selectViewMode,
  selectAllUserMaps,
  restoreLastViewedMap,
} from "./store/mapsSlice";
import {
  fetchUserSettings,
  initializeDefaultSettings,
} from "./store/userSettingsSlice";
import LoginForm from "./components/Auth/LoginForm";
import InteractiveMap from "./components/Map/InteractiveMap";
import MapTabsNavigation from "./components/Maps/MapTabsNavigation";
import MapGridView from "./components/Maps/MapGridView";
import UtilityCard from "./components/Utility/UtilityCard";
import DominantMonitoringAdmin from "./components/Admin/DominantMonitoringAdmin";
import socketService from "./services/socketService";
import "./App.css";

function App() {
  const dispatch = useDispatch<AppDispatch>();
  const { isAuthenticated } = useSelector((state: RootState) => state.auth);
  const { language, isRTL, isUtilityCardMinimized } = useSelector(
    (state: RootState) => state.ui
  );

  const currentMap = useSelector(selectCurrentMap);
  const viewMode = useSelector(selectViewMode);
  const allMaps = useSelector(selectAllUserMaps);
  const navigate = useNavigate();
  const location = useLocation();

  // Dropdown state
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Track if initial data has been loaded to prevent re-initialization
  const initialDataLoadedRef = useRef(false);

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Sync document direction when language changes
  useEffect(() => {
    document.documentElement.dir = isRTL ? "rtl" : "ltr";
  }, [isRTL]);

  useEffect(() => {
    // Only load data if user is authenticated
    if (!isAuthenticated) {
      initialDataLoadedRef.current = false;
      return;
    }

    // Prevent re-running if already loaded
    if (initialDataLoadedRef.current) {
      return;
    }

    // Load initial data from MongoDB and hybrid system
    const loadInitialData = async () => {
      // Check if maps are already loaded to prevent duplicate requests
      const currentState = store.getState();

      if (currentState.maps.loaded) {
        initialDataLoadedRef.current = true;
        return; // Skip if already loaded
      }

      // Continue loading other data even if maps are currently loading
      // This prevents the MapManager's fetchMaps from blocking other data loads

      try {
        // Load configured data from MongoDB (for backward compatibility)
        await dispatch(fetchGateways());
        await dispatch(fetchConfiguredDevices());
        await dispatch(fetchTextLabels());
        await dispatch(fetchSettings());

        // Load user settings
        try {
          await dispatch(fetchUserSettings());
        } catch (error) {
          // If user settings don't exist, initialize with defaults
          // Use current language from store to avoid dependency issues
          const currentLang = store.getState().ui.language;
          dispatch(initializeDefaultSettings(currentLang));
        }

        // Load maps data
        await dispatch(fetchMaps());
        await dispatch(fetchSharedMaps());

        // Restore last viewed map after maps are loaded
        dispatch(restoreLastViewedMap());

        // Load monitored devices and gateways from hybrid system
        await dispatch(fetchMonitoredGateways());
        await dispatch(fetchMonitoredDevices());

        // Process monitored devices into entities for map display
        const currentState = store.getState();
        const monitoredDevices = currentState.devices.monitored || [];

        // Display monitored devices with their stored positions
        const devicesForDisplay: any = {};
        monitoredDevices.forEach((device) => {
          devicesForDisplay[device.mac] = {
            mac: device.mac,
            name:
              device.customSettings?.displayName || device.name || device.mac,
            position: device.position, // Use stored position from database
            lastSeen: device.lastSeenInAPI,
            strongestGateway: device.strongestGateway,
            rssi: device.rssi,
            // Include signal data for SignalStrengthIndicator
            signal: device.signal,
            lastSignalData: device.lastSignalData,
            // Movement properties for smooth animation
            movementAngle: device.movementAngle,
            targetPosition: device.targetPosition,
            movementSpeed: device.movementSpeed || 0.8,
          };
        });

        dispatch(
          updateDevicesFromApi({
            devices: devicesForDisplay,
            gateways: currentState.gateways.entities,
          })
        );
      } catch (error) {
        console.error("Failed to load initial data:", error);
      }

      // Mark as loaded after completion (success or failure)
      initialDataLoadedRef.current = true;
    };

    loadInitialData();
  }, [dispatch, isAuthenticated]); // language intentionally excluded to prevent re-initialization

  // Safety net: Load monitored devices if somehow not loaded (should rarely trigger)
  useEffect(() => {
    if (!isAuthenticated) {
      return;
    }

    const currentState = store.getState();
    if (currentState.devices.monitoredLoaded) {
      return; // Already loaded, skip
    }

    // This should rarely run since processing now happens in the first useEffect
    console.warn("⚠️ Safety net triggered: Loading monitored devices");
    dispatch(fetchMonitoredDevices());
  }, [dispatch, isAuthenticated]);

  // Set up polling interval for device updates (every 10 seconds)
  useEffect(() => {
    if (!isAuthenticated) {
      return;
    }

    // Start polling every 10 seconds
    const pollInterval = setInterval(async () => {
      try {
        // Fetch latest monitored devices with fresh signals from Update API
        const result = await dispatch(
          fetchMonitoredDevices({ freshSignals: true })
        );

        if (fetchMonitoredDevices.fulfilled.match(result)) {
          // Process monitored devices and update positions
          const currentState = store.getState();
          const monitoredDevices = result.payload.devices || [];

          // Update devices with their latest positions
          const devicesForDisplay: any = {};
          monitoredDevices.forEach((device) => {
            devicesForDisplay[device.mac] = {
              mac: device.mac,
              name:
                device.customSettings?.displayName || device.name || device.mac,
              position: device.position,
              lastSeen: device.lastSeenInAPI,
              strongestGateway: device.strongestGateway,
              rssi: device.rssi,
              // Include signal data for SignalStrengthIndicator
              signal: device.signal,
              lastSignalData: device.lastSignalData,
              movementAngle: device.movementAngle,
              targetPosition: device.targetPosition,
              movementSpeed: device.movementSpeed || 0.8,
            };
          });

          // Update Redux store with fresh device data
          dispatch(
            updateDevicesFromApi({
              devices: devicesForDisplay,
              gateways: currentState.gateways.entities,
            })
          );
        }
      } catch (error) {
        console.error("Polling error:", error);
        // Continue polling despite errors
      }
    }, 5000); // Poll every 5 seconds

    // Cleanup on unmount
    return () => {
      clearInterval(pollInterval);
    };
  }, [dispatch, isAuthenticated]);

  // Set up Socket.io connection for real-time updates (independent of state changes)
  useEffect(() => {
    if (!isAuthenticated) {
      return;
    }

    // Set up Socket.io connection for real-time updates
    const setupSocketConnection = async () => {
      try {
        // console.log("🔌 Connecting to Socket.io server...");

        // Connect to Socket.io server with authentication
        await socketService.connect();

        // console.log("✅ Socket.io connection established successfully");

        // Set up device update event listener
        const handleDeviceUpdate = (event: Event) => {
          try {
            const customEvent = event as CustomEvent;
            const data = customEvent.detail;
            // console.log("📡 Received device update via Socket.io:", data.data.mac);

            // Handle device updates with pre-calculated positions
            if (data.data.mac) {
              // console.log("📱 Processing device update via Socket.io:", data.data);

              // Update device in Redux store with Socket.io data (including position if calculated)
              dispatch(
                updateDeviceFromSSE({
                  mac: data.data.mac,
                  name: data.data.name,
                  signal: data.data.signal,
                  lastSeen: data.data.lastSeen,
                  timestamp: data.data.timestamp,
                })
              );

              // If position was calculated on backend, update it directly
              if (data.data.position) {
                // console.log(
                //   `📍 Received calculated position for device ${data.data.mac}:`,
                //   data.data.position
                // );

                const currentDevices = store.getState().devices.entities;
                const existingDevice = currentDevices[data.data.mac];

                if (existingDevice) {
                  dispatch(
                    recalculateDevicePositions({
                      [data.data.mac]: {
                        ...existingDevice,
                        position: data.data.position,
                        strongestGateway: data.data.strongestGateway,
                        rssi: data.data.rssi,
                      },
                    })
                  );
                }
              }
            }
          } catch (error) {
            console.error("Error processing Socket.io device update:", error);
          }
        };

        // Add event listener for device updates
        window.addEventListener("deviceUpdate", handleDeviceUpdate);

        // Store cleanup function
        return () => {
          window.removeEventListener("deviceUpdate", handleDeviceUpdate);
          socketService.disconnect();
        };
      } catch (error) {
        console.error("❌ Failed to establish Socket.io connection:", error);
        // Could implement fallback to polling here if needed
        return () => {}; // Return empty cleanup function
      }
    };

    // Connect to Socket.io for real-time updates
    let cleanupSocket: (() => void) | undefined;
    setupSocketConnection().then((cleanup) => {
      cleanupSocket = cleanup;
    });

    // Cleanup on unmount
    return () => {
      if (cleanupSocket) {
        cleanupSocket();
      }
      socketService.disconnect();
    };
  }, [dispatch, isAuthenticated]);

  // Fetch map-specific gateways when current map changes
  useEffect(() => {
    if (!isAuthenticated || !currentMap) {
      return;
    }

    dispatch(fetchMapGateways(currentMap._id));
  }, [dispatch, isAuthenticated, currentMap]);

  const handleLanguageChange = (lang: "en" | "he") => {
    dispatch(setLanguage(lang));
  };

  // Main App Component with Header (requires authentication)
  const MainApp = () => {
    if (!isAuthenticated) {
      return <LoginForm />;
    }

    return (
      <div
        className={`App min-h-screen bg-gray-50 flex flex-col ${
          isRTL ? "rtl" : "ltr"
        }`}
        dir={isRTL ? "rtl" : "ltr"}
      >
        <header className="bg-white shadow-lg border-b border-gray-200 flex-shrink-0">
          <div className="max-w-full mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between items-center py-3">
              <div className="flex items-center">
                <img
                  src="/logo.png"
                  alt="SwitchQ Logo"
                  className="h-8 w-8 mr-3"
                />
                <h1 className="text-2xl font-bold bg-gradient-to-r from-purple-600 to-fuchsia-700 bg-clip-text text-transparent">
                  SwitchQ RTLS
                </h1>
              </div>
              <div className="flex items-center space-x-2 rtl:space-x-reverse">
                <button
                  onClick={() => dispatch(toggleUtilityCardMinimized())}
                  className="p-2 text-gray-600 hover:text-gray-800 transition-colors duration-200"
                  title={
                    isUtilityCardMinimized
                      ? language === "he"
                        ? "הצג כלי ניהול"
                        : "Show Tools"
                      : language === "he"
                      ? "הסתר כלי ניהול"
                      : "Hide Tools"
                  }
                >
                  <Icon
                    path={isUtilityCardMinimized ? mdiMenu : mdiMenuClose}
                    size={1}
                  />
                </button>

                <div className="relative" ref={dropdownRef}>
                  <button
                    onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                    className="p-2 text-gray-600 hover:text-gray-800 transition-colors duration-200"
                    title={language === "he" ? "תפריט" : "Menu"}
                  >
                    <Icon path={mdiDotsVertical} size={1} />
                  </button>

                  {isDropdownOpen && (
                    <div className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg ring-1 ring-black ring-opacity-5 z-50">
                      <div className="py-1">
                        <button
                          onClick={() => {
                            handleLanguageChange(
                              language === "en" ? "he" : "en"
                            );
                            setIsDropdownOpen(false);
                          }}
                          className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 hover:text-gray-900"
                        >
                          <Icon
                            path={mdiTranslate}
                            size={0.75}
                            className="mr-3"
                          />
                          {language === "en" ? "עברית" : "English"}
                        </button>
                        <button
                          onClick={() => {
                            // Reset all user-specific state to prevent cross-user data leakage
                            dispatch(resetMapsState());
                            dispatch(resetDevicesState());
                            dispatch(resetGatewaysState());
                            dispatch(logout());
                            setIsDropdownOpen(false);
                          }}
                          className="flex items-center w-full px-4 py-2 text-sm text-red-600 hover:bg-red-50 hover:text-red-700"
                        >
                          <Icon path={mdiLogout} size={0.75} className="mr-3" />
                          {language === "he" ? "התנתק" : "Logout"}
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </header>

        <main className="flex-1 flex flex-col overflow-hidden">
          <div className="flex-1 flex flex-col p-2 sm:p-4 lg:p-6">
            <div className="flex-1 flex gap-4 flex-col lg:flex-row">
              {/* Map Card */}
              <div
                className={`bg-white rounded-lg shadow-lg overflow-hidden flex-1 flex flex-col transition-all duration-300 ${
                  isUtilityCardMinimized ? "lg:w-full" : "lg:w-2/3"
                }`}
              >
                {/* Map Navigation */}
                {allMaps.length > 0 && <MapTabsNavigation />}

                <div className="relative flex-1">
                  {viewMode === "grid" ? (
                    <MapGridView key="grid-view" />
                  ) : (
                    <InteractiveMap
                      key={`map-${currentMap?._id || "default"}`}
                    />
                  )}
                </div>
              </div>

              {/* Utility Card - Only show when not minimized and not in grid view */}
              {!isUtilityCardMinimized && viewMode === "tabs" && (
                <div className="bg-white rounded-lg shadow-lg overflow-y-auto flex flex-col lg:w-1/3 min-h-[400px] lg:min-h-0 max-h-[90vh] transition-all duration-300">
                  <div className="p-3 border-b border-gray-200 flex-shrink-0">
                    <h2 className="text-lg font-semibold bg-gradient-to-tr from-violet-600 to-yellow-600 bg-clip-text text-transparent">
                      {language === "he" ? "כלי ניהול" : "Management Tools"}
                    </h2>
                    <p className="text-gray-600 text-sm">
                      {language === "he"
                        ? "נהל התקנים, שערים והגדרות"
                        : "Manage devices, gateways and settings"}
                    </p>
                  </div>

                  <div className="flex-1">
                    <UtilityCard />
                  </div>
                </div>
              )}
            </div>
          </div>
        </main>
      </div>
    );
  };

  // Return Routes - Admin route bypasses main auth, has its own PasswordGate
  return (
    <Routes>
      <Route path="/" element={<MainApp />} />
      <Route
        path="/admin/dominant-monitoring"
        element={<DominantMonitoringAdmin />}
      />
    </Routes>
  );
}

export default App;
